import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage, type LanguageCode } from "@/contexts/LanguageContext";

export interface SlugMapping {
  entity_type: string;
  entity_id: string;
  language: string;
  slug: string;
  canonical_slug: string;
}

/**
 * Fetches all localized slug mappings from the database.
 * Cached for 1 hour since slugs rarely change.
 */
export function useLocalizedSlugs() {
  return useQuery({
    queryKey: ["localized-slugs"],
    queryFn: async () => {
      const allSlugs: SlugMapping[] = [];
      const pageSize = 1000;
      let from = 0;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from("localized_slugs")
          .select("entity_type, entity_id, language, slug, canonical_slug")
          .range(from, from + pageSize - 1);
        if (error) throw error;

        for (const row of data ?? []) {
          allSlugs.push(row as SlugMapping);
        }

        hasMore = (data?.length ?? 0) === pageSize;
        from += pageSize;
      }

      return allSlugs;
    },
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 2 * 60 * 60 * 1000,
  });
}

/**
 * Builds localized URL paths based on the current language.
 */
export function useLocalizedUrl() {
  const { language } = useLanguage();
  const { data: slugs } = useLocalizedSlugs();

  /**
   * Convert a canonical path like "/experiences/kayak-tour"
   * into a localized path like "/sv/upplevelser/kajak-tur"
   */
  function localizeUrl(canonicalPath: string, targetLang?: LanguageCode): string {
    const lang = targetLang ?? language;
    if (lang === "en") return canonicalPath; // English uses canonical paths

    if (!slugs?.length) {
      // Fallback: just prefix with lang
      return `/${lang}${canonicalPath}`;
    }

    const segments = canonicalPath.split("/").filter(Boolean);
    const localizedSegments: string[] = [];

    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];

      // Try route_segment lookup first
      const routeMapping = slugs.find(
        (s) => s.entity_type === "route_segment" && s.entity_id === segment && s.language === lang
      );
      if (routeMapping) {
        localizedSegments.push(routeMapping.slug);
        continue;
      }

      // Try entity slug lookup (experience slug, destination slug)
      const entityMapping = slugs.find(
        (s) => s.entity_type !== "route_segment" && s.canonical_slug === segment && s.language === lang
      );
      if (entityMapping) {
        localizedSegments.push(entityMapping.slug);
        continue;
      }

      // Keep as-is (unknown segment or already localized)
      localizedSegments.push(segment);
    }

    return `/${lang}/${localizedSegments.join("/")}`;
  }

  /**
   * Resolve a localized path back to its canonical form.
   * e.g. "/sv/upplevelser/kajak-tur" → { lang: "sv", canonicalPath: "/experiences/kayak-tour" }
   */
  function resolveLocalizedUrl(path: string): { lang: LanguageCode | null; canonicalPath: string } {
    const segments = path.split("/").filter(Boolean);
    if (segments.length === 0) return { lang: null, canonicalPath: "/" };

    // Check if first segment is a language code
    const SUPPORTED_LANGS = ["en", "sv", "no", "da", "fi", "de", "nl", "fr", "es", "it"];
    const firstSegment = segments[0];
    if (!SUPPORTED_LANGS.includes(firstSegment)) {
      return { lang: null, canonicalPath: path }; // Not a localized URL
    }

    const lang = firstSegment as LanguageCode;
    const restSegments = segments.slice(1);

    if (!slugs?.length) {
      return { lang, canonicalPath: `/${restSegments.join("/")}` };
    }

    const canonicalSegments: string[] = [];

    for (const segment of restSegments) {
      // Try reverse route_segment lookup
      const routeMapping = slugs.find(
        (s) => s.entity_type === "route_segment" && s.slug === segment && s.language === lang
      );
      if (routeMapping) {
        canonicalSegments.push(routeMapping.canonical_slug);
        continue;
      }

      // Try reverse entity slug lookup
      const entityMapping = slugs.find(
        (s) => s.entity_type !== "route_segment" && s.slug === segment && s.language === lang
      );
      if (entityMapping) {
        canonicalSegments.push(entityMapping.canonical_slug);
        continue;
      }

      canonicalSegments.push(segment);
    }

    return { lang, canonicalPath: `/${canonicalSegments.join("/")}` };
  }

  return { localizeUrl, resolveLocalizedUrl, slugs };
}
